import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import type { Progression } from '../../game/Progression';
import type { QuestDef } from '../../game/quests';
import type { ZoneLayout } from '../../zones/types';
import { addTitle } from './titles';

/**
 * Explorer's Journal (≈ Adventurer's Tome): per-zone completion from the
 * roster's collectibles (Glimmerseeds, Hearts, vistas, lore, field bosses),
 * the character's story dungeon clears and its quests in the zone. Rewards
 * are claimed at 10/30/50/70/100% and recorded roster-wide in
 * `account.achievements` under `journal.<zone>.<pct>`.
 */

export type JournalCategoryId =
  'seeds' | 'hearts' | 'vistas' | 'lore' | 'bosses' | 'dungeons' | 'quests';

export interface JournalEntry {
  id: string;
  name: string;
  done: boolean;
}

export interface JournalCategory {
  id: JournalCategoryId;
  name: string;
  have: number;
  total: number;
  entries: JournalEntry[];
}

export interface ZoneJournal {
  zone: string;
  name: string;
  kind: string;
  levelRange: [number, number];
  categories: JournalCategory[];
  have: number;
  total: number;
  /** Whole-percent completion 0..100. */
  pct: number;
}

export interface JournalSources {
  /** Dungeon layouts reachable from the zone (for clears). */
  dungeon?: (id: string) => ZoneLayout | null;
  quests?: QuestDef[];
  /** Display name for a monster id. */
  monsterName?: (id: string) => string;
}

const CATEGORY_NAMES: Record<JournalCategoryId, string> = {
  seeds: 'Glimmerseeds',
  hearts: 'Hearts',
  vistas: 'Vistas',
  lore: 'Lore',
  bosses: 'Field bosses',
  dungeons: 'Dungeons',
  quests: 'Quests',
};

/** A story dungeon counts as cleared by its `<id>_cleared` flag or a kill of its boss. */
export function dungeonCleared(c: CharacterSave, id: string, layout: ZoneLayout | null): boolean {
  if (c.flags[`${id}_cleared`]) return true;
  const bosses =
    layout?.packs
      .filter((p) => p.tags?.includes('boss'))
      .flatMap((p) => p.monsters.map((m) => m.def)) ?? [];
  return bosses.some((b) => (c.counters[`kill_${b}`] ?? 0) > 0);
}

export function zoneJournal(
  layout: ZoneLayout,
  a: AccountSave,
  c: CharacterSave,
  src: JournalSources = {},
): ZoneJournal {
  const col = a.collectibles;
  const cats: JournalCategory[] = [];
  const push = (id: JournalCategoryId, entries: JournalEntry[]) => {
    if (entries.length)
      cats.push({
        id,
        name: CATEGORY_NAMES[id],
        have: entries.filter((e) => e.done).length,
        total: entries.length,
        entries,
      });
  };
  const ofKind = (kind: string, list: string[], label: (i: number, name?: string) => string) =>
    layout.interactables
      .filter((it) => it.kind === kind)
      .map((it, i) => ({
        id: it.id,
        name: label(i, it.data?.name ? String(it.data.name) : undefined),
        done: list.includes(it.id),
      }));

  push(
    'seeds',
    ofKind('seed', col.seeds, (i) => `Glimmerseed ${i + 1}`),
  );
  push(
    'hearts',
    ofKind('heart', col.hearts, (_i, n) => n ?? 'Heart'),
  );
  push(
    'vistas',
    ofKind('vista', col.vistas, (_i, n) => n ?? 'Vista'),
  );
  push(
    'lore',
    ofKind('lore', col.lore, (i) => `Lore fragment ${i + 1}`),
  );

  const bosses = new Set<string>();
  for (const p of layout.packs)
    if (p.tags?.includes('fieldBoss')) for (const m of p.monsters) bosses.add(m.def);
  push(
    'bosses',
    [...bosses].map((id) => ({
      id,
      name: src.monsterName?.(id) ?? id,
      done: col.fieldBosses.includes(id),
    })),
  );

  push(
    'dungeons',
    layout.portals
      .filter((pt) => pt.kind === 'dungeon')
      .map((pt) => {
        const d = src.dungeon?.(pt.to) ?? null;
        return { id: pt.to, name: d?.name ?? pt.label, done: dungeonCleared(c, pt.to, d) };
      }),
  );

  push(
    'quests',
    (src.quests ?? [])
      .filter((q) => q.zone === layout.id && (q.kind === 'main' || q.kind === 'side'))
      .map((q) => ({ id: q.id, name: q.name, done: c.quests.done.includes(q.id) })),
  );

  const have = cats.reduce((s, x) => s + x.have, 0);
  const total = cats.reduce((s, x) => s + x.total, 0);
  return {
    zone: layout.id,
    name: layout.name,
    kind: layout.kind,
    levelRange: [layout.levelRange[0], layout.levelRange[1]],
    categories: cats,
    have,
    total,
    pct: total ? Math.floor((have / total) * 100) : 0,
  };
}

// ---------------------------------------------------------------- milestones

export interface JournalReward {
  silver?: number;
  gold?: number;
  items?: { id: string; count: number }[];
  title?: string;
}

export const JOURNAL_MILESTONES: { pct: number; reward: JournalReward }[] = [
  { pct: 10, reward: { silver: 1000, items: [{ id: 'phoenix_plume', count: 2 }] } },
  { pct: 30, reward: { silver: 2500, items: [{ id: 'potion_major', count: 5 }] } },
  { pct: 50, reward: { items: [{ id: 'skill_potion', count: 1 }] } },
  { pct: 70, reward: { silver: 5000, gold: 25 } },
  { pct: 100, reward: { items: [{ id: 'skill_potion', count: 1 }] } },
];

/** "Emberwild Vale" → "Emberwild Pathfinder". */
export const journalTitle = (zoneName: string): string =>
  `${zoneName.split(' ')[0] ?? zoneName} Pathfinder`;

export function milestoneReward(j: Pick<ZoneJournal, 'name'>, pct: number): JournalReward {
  const base = JOURNAL_MILESTONES.find((m) => m.pct === pct)?.reward ?? {};
  return pct === 100 ? { ...base, title: journalTitle(j.name) } : base;
}

const claimKey = (zone: string, pct: number) => `journal.${zone}.${pct}`;
export const milestoneClaimed = (a: AccountSave, zone: string, pct: number): boolean =>
  !!a.achievements[claimKey(zone, pct)];

/** Claims a reached, unclaimed milestone; returns the reward or null. */
export function claimMilestone(
  p: Progression,
  j: ZoneJournal,
  pct: number,
  now = Date.now(),
): JournalReward | null {
  if (
    j.pct < pct ||
    milestoneClaimed(p.account, j.zone, pct) ||
    !JOURNAL_MILESTONES.some((m) => m.pct === pct)
  )
    return null;
  const r = milestoneReward(j, pct);
  p.account.achievements[claimKey(j.zone, pct)] = now;
  p.account.wallet.silver += r.silver ?? 0;
  p.account.wallet.gold += r.gold ?? 0;
  for (const it of r.items ?? []) p.giveItem(it.id, it.count);
  if (r.title) addTitle(p.char, r.title);
  p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  return r;
}

/** Titles the roster earned from 100% journals. */
export function journalTitles(a: AccountSave, zones: { id: string; name: string }[]): string[] {
  return zones.filter((z) => milestoneClaimed(a, z.id, 100)).map((z) => journalTitle(z.name));
}
